/**
 * plotStyleOverride — PDF-6.3b CTB/STB application in the PDF entity walk.
 *
 * The `@dxf-ai-editor/acadctb` parser (PDF-6.3a) decodes a `.ctb` (color-
 * dependent) or `.stb` (named) plot-style table into a typed
 * {@link PlotStyleTable}. This module is the consumer that, given an entity's
 * resolved ACI (CTB) or named-style key (STB), looks up the matching
 * {@link PlotStyleEntry} and projects its overrides onto the base resolved
 * {@link PlotStyle}.
 *
 * # AutoCAD-parity application paths (4)
 *
 *   1. **Color** — `entry.colorPolicy === 1` AND `entry.color.useObject === false`
 *      replaces the resolved plot RGB by `entry.color.{r,g,b}`. Policy `0` or
 *      `useObject === true` leaves the entity colour alone.
 *   2. **Lineweight** — `entry.lineweightIndex !== 0` looks up
 *      `table.customLineweights[lineweightIndex]` (mm) and replaces the resolved
 *      mm value. Index `0` is AutoCAD's "use object lineweight" sentinel — no
 *      override.
 *   3. **Screening → alpha** — `screen ∈ (0, 100)` projects to `alpha = screen/100`
 *      so the emitter can wire it through a PDF ExtGState (`/CA` stroke + `/ca`
 *      fill). `screen === 100` is fully opaque (no ExtGState needed — `alpha`
 *      stays `undefined`). `screen <= 0` is treated as DO NOT PLOT.
 *   4. **Do-not-plot** — `entry.plot === false` OR `screen <= 0` returns
 *      `applied.skip = true`. The converter short-circuits to an empty array
 *      and the sheet emitter skips the OCG wrapper + paint operators
 *      (defence-in-depth).
 *
 * # Linetype override
 *
 * `entry.linetypeIndex !== 31` (the "use object linetype" sentinel) overrides
 * the resolved linetype name via a fixed AutoCAD-standard index → name map. CTB
 * linetype 0 = Solid; 1..30 are the standard acad.lin dash patterns. Unknown
 * indices (e.g. malformed CTB) leave the entity linetype unchanged — we never
 * fabricate a name (CLAUDE.md PRIORITY #1).
 *
 * # OMITTED (documented, NOT faked)
 *
 *   - Fill style overrides — only relevant to HATCH, deferred to PDF-6.5.
 *   - End-style / Join-style overrides — PDF caps + joins are already
 *     batch-set by the emitter; per-entry overrides deferred.
 *   - Adaptive-linetype (`entry.adaptiveLinetype`) — deferred until LTYPE
 *     scaling has a per-entity hook.
 *   - Per-entity `plotStyleName` on the entity model — the model currently
 *     stores `plotStyleName` only on the LAYOUT (coarse table choice) and at
 *     the layer level on import; a true per-entity field is a future model
 *     extension. STB lookup here therefore consults the layer's name (passed
 *     in by the caller) or the layout's table name when nothing else applies.
 */

import type {
  PlotStyleEntry,
  PlotStyleTable,
} from "@dxf-ai-editor/acadctb";

import type { RgbColor } from "../pdfTransform";

/**
 * Mapping from a CTB / STB `entry.linetypeIndex` to the AutoCAD-standard
 * linetype name. Index `31` (out of range here — handled separately by the
 * caller) is the "use object linetype" sentinel; indices 0..30 are the fixed
 * acad.lin standard patterns. An index outside this range leaves the entity
 * linetype unchanged (no fallback fabrication).
 *
 * Source: AutoCAD PlotStyle linetype index reference (matches `ezdxf`
 * `acadctb.LINEPATTERNS_TABLE`).
 */
export const LINETYPE_INDEX_NAME_MAP: readonly string[] = [
  "Solid", // 0
  "Dashed", // 1
  "Dotted", // 2
  "Dash dot", // 3
  "Short dash", // 4
  "Medium dash", // 5
  "Long dash", // 6
  "Short dash x2", // 7
  "Medium dash x2", // 8
  "Long dash x2", // 9
  "Medium long dash", // 10
  "Medium dash short dash short dash", // 11
  "Long dash short dash", // 12
  "Long dash dot dot", // 13
  "Long dash dot", // 14
  "Medium dash dot short dash dot", // 15
  "Sparse dot", // 16
  "ISO long dash", // 17
  "ISO long dash space", // 18
  "ISO long dash short dash", // 19
  "ISO long dash double short dash", // 20
  "ISO dash", // 21
  "ISO dash space", // 22
  "ISO long dash dot", // 23
  "ISO long dash double dot", // 24
  "ISO long dash triple dot", // 25
  "ISO dot", // 26
  "ISO long dash short dash short dash", // 27
  "ISO double dash dot", // 28
  "ISO double dash double dot", // 29
  "ISO double dash triple dot", // 30
];

/**
 * Resolved override snapshot the caller folds into a {@link PlotStyle}. Each
 * channel is undefined when the entry does NOT contribute an override (so the
 * caller's `applied.x ?? base.x` collapses to identity for unaffected fields).
 *
 * `skip === true` is the do-not-plot sentinel; the caller short-circuits to an
 * empty primitive list and the sheet emitter additionally guards in its OCG
 * loop (defence-in-depth).
 */
export interface PlotStyleApplied {
  /** Stroke + fill colour override (0..1 RGB), or undefined when no override. */
  color?: RgbColor;
  /** Lineweight override in mm, or undefined when no override. */
  lineweightMm?: number;
  /** Linetype name override, or undefined when no override. */
  linetype?: string;
  /**
   * Stroke + fill alpha in (0, 1] derived from `screen ∈ (0, 100)`. Undefined
   * for `screen === 100` (fully opaque — no ExtGState needed).
   */
  alpha?: number;
  /**
   * True when the entry says "do not plot" (`plot === false` or `screen <= 0`).
   * The caller returns an empty primitive list; the emitter additionally guards
   * its OCG loop on the same flag.
   */
  skip: boolean;
}

/**
 * Look up the {@link PlotStyleEntry} that applies to an entity.
 *
 * - **CTB**: positional — `entries[aci - 1]`. ACI 1..256 maps to indices 0..255.
 *   An invalid / missing ACI (BYLAYER unresolved, BYBLOCK at root, out-of-range)
 *   returns null (no override).
 * - **STB**: named — exact-case match by `entry.name`. When `plotStyleName` is
 *   omitted/empty, the "Normal" entry (always present in well-formed STBs) is
 *   used as the default.
 *
 * Returns null when the table is absent, the lookup key is missing, or no
 * entry matches.
 */
export function pickPlotStyleEntry(
  table: PlotStyleTable | null | undefined,
  aci: number | null | undefined,
  plotStyleName: string | null | undefined,
): PlotStyleEntry | null {
  if (!table) return null;

  if (table.kind === "ctb") {
    if (typeof aci !== "number" || !Number.isInteger(aci)) return null;
    if (aci < 1 || aci > 256) return null;
    return table.entries[aci - 1] ?? null;
  }

  // STB lookup — exact-name match, defaulting to "Normal" when no name supplied.
  const key =
    typeof plotStyleName === "string" && plotStyleName.trim() !== ""
      ? plotStyleName.trim()
      : "Normal";
  const match = table.entries.find((e) => e.name === key);
  if (match) return match;
  // Fall back to "Normal" if the named entry was missing but Normal exists
  // (well-formed STBs guarantee it). If even Normal is absent, no override.
  if (key !== "Normal") {
    const normal = table.entries.find((e) => e.name === "Normal");
    if (normal) return normal;
  }
  return null;
}

/**
 * Project a {@link PlotStyleEntry} onto a {@link PlotStyleApplied} delta the
 * caller merges into the entity's base {@link PlotStyle}.
 *
 * Application rules (see file doc):
 *   - colour: `entry.colorPolicy === 1 && !entry.color.useObject` → override.
 *   - lineweight: `entry.lineweightIndex > 0` → look up
 *     `table.customLineweights[i]`. Index `0` is the "use object lineweight"
 *     sentinel; a non-finite / non-positive table value also leaves the entity
 *     lineweight alone.
 *   - linetype: `entry.linetypeIndex !== 31` and the index is in
 *     `LINETYPE_INDEX_NAME_MAP` → override; otherwise unchanged.
 *   - screen: `screen <= 0` → skip; `screen ∈ (0, 100)` → alpha; `screen >= 100`
 *     → opaque (alpha stays undefined).
 *   - plot: `entry.plot === false` → skip.
 */
export function applyPlotStyleEntry(
  entry: PlotStyleEntry,
  table: PlotStyleTable,
): PlotStyleApplied {
  // Do-not-plot short-circuit. Either explicit `plot=false` or a screen
  // percentage that collapses to invisible. We still compute the rest of the
  // fields cheaply, but the caller checks `skip` first.
  const skip = entry.plot === false || entry.screen <= 0;

  const applied: PlotStyleApplied = { skip };

  // (1) Colour override: explicit policy + non-useObject. CTB stores the
  // override as 8-bit channels (0..255); pdf-lib (and our `RgbColor`) wants
  // normalised 0..1 components.
  if (entry.colorPolicy === 1 && entry.color.useObject === false) {
    applied.color = {
      r: clamp01(entry.color.r / 255),
      g: clamp01(entry.color.g / 255),
      b: clamp01(entry.color.b / 255),
    };
  }

  // (2) Lineweight override: index 0 is the "use object lineweight" sentinel.
  // A non-finite / non-positive value in the table also leaves the entity
  // lineweight unchanged (defensive — the parser validates, but a malformed
  // table should not crash the export).
  if (
    entry.lineweightIndex > 0 &&
    entry.lineweightIndex < table.customLineweights.length
  ) {
    const lw = table.customLineweights[entry.lineweightIndex];
    if (typeof lw === "number" && Number.isFinite(lw) && lw > 0) {
      applied.lineweightMm = lw;
    }
  }

  // (3) Linetype override: 31 is the "use object linetype" sentinel. Anything
  // outside the standard 0..30 range leaves the entity linetype alone (we do
  // not fabricate a name — CLAUDE.md PRIORITY #1).
  if (
    entry.linetypeIndex !== 31 &&
    entry.linetypeIndex >= 0 &&
    entry.linetypeIndex < LINETYPE_INDEX_NAME_MAP.length
  ) {
    applied.linetype = LINETYPE_INDEX_NAME_MAP[entry.linetypeIndex];
  }

  // (4) Screening → alpha. Only project values strictly inside (0, 100); 100
  // is opaque (no ExtGState) and ≤0 is folded into `skip` above. We round to
  // 3 decimal places so the emitter's resource registry dedups stably across
  // floating-point noise.
  if (entry.screen > 0 && entry.screen < 100) {
    applied.alpha = Math.round((entry.screen / 100) * 1000) / 1000;
  }

  return applied;
}

/**
 * One-step convenience: pick the matching entry, then apply it. Returns null
 * when no entry matches (table absent, key missing, etc.) so the caller can
 * skip the merge with one branch.
 */
export function resolveOverrideForEntity(
  table: PlotStyleTable | null | undefined,
  aci: number | null | undefined,
  plotStyleName: string | null | undefined,
): PlotStyleApplied | null {
  if (!table) return null;
  const entry = pickPlotStyleEntry(table, aci, plotStyleName);
  if (!entry) return null;
  return applyPlotStyleEntry(entry, table);
}

/** Clamp a number to [0, 1] (defensive — CTB channels are validated 0..255). */
function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}
